<script lang="jsx">
  import { ref, toRefs, watch, provide, nextTick } from '@vue/composition-api'
  import TableBar from './components/table-bar'
  import TableFooter from './components/table-footer'
  import TableColumnTip from './components/table-column-tip'
  import YdColumn from './yd-column.vue'
  import { useDataSource, useColumns, useTable, useTableAffix } from './hooks'
  import { validateSettings, validateSize } from './utils/utils.props'
  import { uuidv5 } from '@/util'
  export default {
    name: 'YdTable',
    components: {
      TableBar,
      TableFooter,
      TableColumnTip,
      YdColumn
    },
    props: {
      // 数据源
      data: {
        type: Array,
        default() {
          return []
        }
      },
      // 表格列
      columns: {
        type: Array,
        default() {
          return []
        }
      },
      loading: {
        type: Boolean
        // default: false
      },
      // 表格底部需要处理的行
      footerNumber: {
        type: Number,
        default: 0
      },

      // 表格大小，用于控制表格紧凑程度
      size: {
        type: String,
        validate: validateSize,
        default: 'medium'
      },
      // 是否显示表格边框
      border: {
        type: Boolean,
        default: true
      },
      // 行key
      rowKey: {
        type: [String, Function],
        default: 'id'
      },
      stripe: {
        type: Boolean,
        default: true
      },
      pagination: {
        type: [Object, Boolean],
        default() {
          return false
          /* {
            // 分页位置 left center right
            position: 'right',
            current: 1,
            total: 0,
            pageSize: 10
          } */
        }
      },
      // 预设配置
      settings: {
        type: [Boolean, Object],
        validate: validateSettings,
        default() {
          return {
            align: true,
            size: true,
            fullScreen: true,
            columns: true,
            showIndex: false,
            showSelect: false
          }
        }
      },
      // 在y轴滚动
      scroll: {
        type: Object,
        default() {
          return {
            y: 0
          }
        }
      },
      fixedTop: {
        type: [Boolean, Object],
        default() {
          return false
        }
      },
      selectable: {
        type: Function,
        default: null
      },
      reserveSelection: {
        type: Boolean,
        default: false
      },
      // 首次onMounted时，触发表格刷新(onChange事件，type为init)
      autoLoad: {
        type: Boolean,
        default: true
      },
      tableId: {
        type: String,
        default() {
          return uuidv5()
        }
      }
    },
    data() {
      return {
        time: null
      }
    },
    emits: ['onChange', 'onInit'],
    methods: {
      doLayout() {
        const allSlots = this.$slots.default || []
        if (allSlots.length > 0) {
          const defaultColumns = []
          allSlots.forEach((item) => {
            const { componentOptions, data = {} } = item
            const options = componentOptions || {}
            const { scopedSlots = {}, attrs = {} } = data || {}
            if (componentOptions) {
              defaultColumns.push({
                ...options.propsData,
                ...attrs,
                checked: true,
                render: scopedSlots.default
              })
            }
          })
          // 将现有columns slot设置成columns数组
          this.setColumns(defaultColumns, true)
        }
        if (this.tableRef) {
          this.tableRef.doLayout()
        }
      },
      initRef() {
        this.tableRef = this.$refs[`tableRef_${this.tableId}`]
        this.tableContainer = this.$refs[`tableContainer_${this.tableId}`]
      }
    },
    mounted() {
      this.initRef()
      this.doLayout()
    },
    setup(fromProps, ctx) {
      const { emit } = ctx
      const props = toRefs(fromProps)
      const { columnsSettings, setTableAlign, renderColumns, setColumns, allColumns } =
        useColumns(props)
      const tableRef = ref(null)

      const tableContainer = ref(null)
      const isTableFullScreen = ref(false)
      // 表格内部处理刷新使用
      const currentPage = ref(null)
      provide('tableProps', props)

      provide('tableContainer', tableContainer)
      provide('tableRef', tableRef)
      provide('isTableFullScreen', isTableFullScreen)
      const { bodyList, bottomList } = useDataSource(props.data, props.footerNumber)
      const { setHeaderTop } = useTableAffix(tableRef, props.fixedTop, isTableFullScreen)
      const {
        setTableBorder,
        tableWidth,
        tableSize,
        tableBorder,
        scrollLeft,
        setScrollLeft,
        tableScroll
      } = useTable(props, tableRef, renderColumns, tableContainer)

      watch(isTableFullScreen, (v) => {
        setHeaderTop(v)
      })
      const onFresh = () => {
        emit('onChange', {
          ...currentPage.value,
          type: 'fresh'
        })
      }
      const initPages = ref({
        pages: props.pagination,
        onChange(v) {
          currentPage.value = v
          const tableChangeInfo = {
            columns: renderColumns,
            elRef: tableRef,
            props,
            setTableAlign,
            setColumns,
            tableSize,
            setScrollLeft
          }
          nextTick(() => {
            emit('onInit', tableChangeInfo)
          })
          if (v.type !== 'init') {
            emit('onChange', v)
          }
          if (props.autoLoad.value && v.type === 'init') {
            emit('onChange', v)
          }
        }
      })
      return {
        setColumns,
        renderColumns,
        bodyList,
        bottomList,
        tableRef,
        tableSize,
        tableBorder,
        onFresh,
        tableContainer,
        isTableFullScreen,
        tableScroll,
        columnsSettings,
        setTableAlign,
        setTableBorder,
        scrollLeft,
        setScrollLeft,
        tableWidth,
        initPages,
        allColumns
      }
    },
    render() {
      const {
        bodyList,
        bottomList,
        tableSize,
        tableBorder,
        isTableFullScreen,
        tableScroll,
        stripe,
        loading,
        rowKey,
        renderColumns,
        tableId,
        columnsSettings,
        setTableAlign,
        setTableBorder,
        scrollLeft,
        setScrollLeft,
        tableWidth,
        initPages,
        allColumns
      } = this
      // console.log('renderColumns: ', JSON.parse(JSON.stringify(renderColumns)))
      const renderTable = () => {
        const renderHtml = renderColumns.map((col) => {
          const { render, formatter, contentAlign = '', ...props } = col
          if (!props.checked) {
            return null
          }
          let renderFn = formatter || render
          if (!render && !formatter) {
            renderFn = ({ row }) => {
              return row[col.prop]
            }
          }
          const key = tableId + '_' + props.id
          if (props.tips) {
            return (
              <el-table-column
                key={key}
                {...{
                  props,
                  scopedSlots: {
                    header: (scope) => {
                      return (
                        <TableColumnTip
                          content={props.tips}
                          label={props.label}
                          align={props.align}
                        />
                      )
                    },
                    default: (scope) => {
                      return this.$scopedSlots[props.prop]
                        ? this.$scopedSlots[props.prop](scope)
                        : renderFn(scope)
                    }
                  }
                }}
              />
            )
          }
          if (props.prop === 'sortIndex') {
            renderFn = ({ $index }) => {
              return $index + 1
            }
            return (
              <el-table-column
                key={key}
                {...{
                  props,
                  scopedSlots: {
                    default: (scope) => renderFn(scope)
                  }
                }}
              />
            )
          }

          if (col.type === 'selection' || (col.children && col.children.length)) {
            return <YdColumn column={props} />
          }

          if (props.prop === 'sortIndex') {
            renderFn = ({ row, column, $index }) => {
              return $index + 1
            }
          }
          return (
            <el-table-column
              key={key}
              {...{
                props,
                scopedSlots: {
                  default: (scope) => {
                    return (
                      <div
                        class={{
                          'text-center': contentAlign === 'center',
                          'text-left': contentAlign === 'left',
                          'text-right': contentAlign === 'right'
                        }}
                      >
                        {this.$scopedSlots[props.prop]
                          ? this.$scopedSlots[props.prop](scope)
                          : renderFn(scope)}
                      </div>
                    )
                  }
                }
              }}
            ></el-table-column>
          )
        })
        return (
          <el-table
            ref={`tableRef_${tableId}`}
            key={tableId}
            id={tableId}
            size={tableSize}
            border={tableBorder}
            stripe={stripe}
            v-loading={loading}
            data={bodyList}
            row-key={rowKey}
            style={{ width: '100%' }}
            attrs={this.$attrs}
            {...{
              on: this.$listeners,
              ...tableScroll
            }}
          >
            {renderHtml.map((col, index) => col)}
          </el-table>
        )
      }

      const renderHeader = () => {
        return (
          <TableBar
            {...{
              on: Object.assign({}, this.$listeners, {
                onFresh: this.onFresh
              })
            }}
            columnsSettings={columnsSettings}
            setTableAlign={setTableAlign}
            setTableBorder={setTableBorder}
            localColumns={allColumns}
            attrs={this.$attrs}
          >
            {Object.keys(this.$slots).map((name) => (
              <template slot={name}>{this.$slots[name]}</template>
            ))}
          </TableBar>
        )
      }
      const renderFooter = () => {
        return (
          <TableFooter
            list={bottomList}
            stripe={stripe}
            index={bodyList.length}
            attrs={this.$attrs}
            tableSize={tableSize}
            tableBorder={tableBorder}
            renderColumns={renderColumns}
            scrollLeft={scrollLeft}
            setScrollLeft={setScrollLeft}
            tableWidth={tableWidth}
            pageState={initPages}
            {...{
              on: this.$listeners,
              ...tableScroll
            }}
          >
            {Object.keys(this.$slots).map((name) => (
              <template slot={name}>{this.$slots[name]}</template>
            ))}
          </TableFooter>
        )
      }
      return (
        <div
          ref={'tableContainer_' + tableId}
          key={tableId}
          id={tableId}
          class={[isTableFullScreen ? 'is-full-screen' : '', 'yd-table-container']}
        >
          {renderHeader()}
          {renderTable()}
          {renderFooter()}
        </div>
      )
    }
  }
</script>
<style scoped lang="scss">
  .yd-table-container {
    background-color: #fff;
    .el-table {
      border-top: none;
      overflow: initial;
    }

    .el-table--border {
      ::v-deep {
        .el-table__header-wrapper {
          box-sizing: border-box;
          border-right: 1px solid #e9ecef;

          .el-table__fixed-right {
            right: -1px;
            left: auto;
          }
        }
      }
    }
    ::v-deep {
      .el-table__body-wrapper {
        &::-webkit-scrollbar {
          width: 0;
        }
      }
    }
    ::v-deep {
      .el-table__fixed {
        // box-shadow: 3px 0 5px rgb(0 0 0 / 12%);
        .el-table__cell.is-leaf {
          border-top: 1px solid #ebeef5;
        }
      }
      .el-table__fixed-right {
        // box-shadow: -3px 0 5px rgb(0 0 0 / 12%);
        .el-table__cell.is-leaf {
          border-top: 1px solid #ebeef5;
        }
      }

      .el-table__header-wrapper {
        position: static;
        border-top: 1px solid #e9ecef;
        z-index: 5;

        .el-table__fixed,
        .el-table__fixed-right {
          position: sticky;
          height: auto;
          flex-shrink: 0;
          z-index: 9;
        }
      }

      .el-table__header-wrapper:has(+ .is-scrolling-right, + .is-scrolling-none) {
        .el-table__fixed-right {
          box-shadow: none;
        }
      }

      .el-table__header-wrapper:has(+ .is-scrolling-left, + .is-scrolling-none) {
        .el-table__fixed {
          box-shadow: none;
        }
      }
    }
    ::v-deep {
      .el-table__header-wrapper {
        .el-table__fixed,
        .el-table__fixed-right {
          height: auto !important;
        }
      }
    }
  }
  .yd-table-container.is-full-screen {
    width: 100%;
    height: 100vh;
    position: fixed;
    overflow: auto;
    top: 0;
    left: 0;
    z-index: 999;
  }
</style>
